import type {
    ActorEvent,
    DataSourceAbstract,
    DevicePreviewEnum,
    FilterCommonCondition,
    GroupConfigure,
    GroupTab,
    IndependentAction,
    KanbanColumnsSort,
    QuickFilterData,
    RichTextContentProtocol,
    Sorter,
    TableColumns,
    ViewBlockAbstract,
    ViewField,
    ViewIndependentData,
    ViewType
} from '@lighthouse/core'
import { CUSTOM_VIEW_LAYOUT, DIRECTION } from '@lighthouse/core'
import { find } from 'rambda'
import React, { useMemo } from 'react'

import type { UserOption, ViewAppendParams } from '../../types'
import { getViewColumns } from '../../utils'
import { getGroupAllVisibleOptions, getGroupVisibleWithLabelOptions, mergeGroupVisibleOptions } from '../DataGroupConfigure'
import { PaginationFc } from '../Pagination'
import { TableTab } from '../TableTab'
import { ViewSettingToolbar } from './ViewSettingToolbar'

export interface ViewBlockToolProps {
    children?: React.ReactNode
    appId: string
    envId?: string
    uniqueId: string
    dataSource: DataSourceAbstract
    dataSourceList: DataSourceAbstract[]
    tabList?: GroupTab[]
    tabValue?: string
    blockData: ViewBlockAbstract
    quickFilterData?: QuickFilterData
    viewIndependentDataOmitRecord: Omit<ViewIndependentData, 'recordPoints'>
    tablePropsCache: TableColumns
    sortCache?: Sorter[]
    groupCache?: GroupConfigure
    personOptions?: UserOption[]
    cachedKanbanSort?: KanbanColumnsSort
    onTabChange?: (val: string) => void
    onSearch?: (val: string) => void
    onFilter?: (data: FilterCommonCondition[]) => void
    onChangeSorter?: (data?: Sorter[]) => void
    onChangeDisplay: (data?: TableColumns) => void
    onChangeGroup?: (data: GroupConfigure) => void
    onAppend?: (params: ViewAppendParams) => Promise<boolean>
    onToolbarActionTrigger?: (id: string, params: ActorEvent) => Promise<void>
    onChangePageNum?: (pageNum: number) => void
    onRenderButtonTitle: (v: RichTextContentProtocol) => string
    onChangeCachedKanbanSort?: (val: KanbanColumnsSort | undefined) => void
}

const enableSortView: Set<ViewType> = new Set(['table', 'advancedTable', 'list', 'gallery', 'kanban', 'custom'])
const enableDisplayView: Set<ViewType> = new Set(['table', 'advancedTable', 'list', 'kanban'])
const enablePagination: Set<ViewType> = new Set(['table', 'list', 'gallery', 'advancedTable', 'custom'])

export const ViewBlockTool: React.FC<ViewBlockToolProps> = ({
    uniqueId,
    children,
    appId,
    envId = '',
    dataSource,
    dataSourceList,
    tabList,
    tabValue,
    blockData,
    quickFilterData,
    viewIndependentDataOmitRecord,
    tablePropsCache,
    sortCache,
    groupCache,
    personOptions,
    cachedKanbanSort,
    onTabChange,
    onSearch,
    onFilter,
    onChangeSorter,
    onChangeDisplay,
    onChangeGroup,
    onAppend,
    onToolbarActionTrigger,
    onChangePageNum,
    onRenderButtonTitle,
    onChangeCachedKanbanSort
}) => {
    const { config, title, id: blockId } = blockData
    const { schema, viewOptions } = dataSource
    const { tableProps } = viewOptions

    const {
        viewType,
        pointer,
        canSearch,
        canGroup,
        canSort,
        canDisplay,
        showTitle,
        toolbar,
        pagination,
        viewFieldSettings,
        userImportSetting,
        userSortSetting,
        canImport,
        canPaginate,
        breakPoint: baseBreakPoint
    } = config

    const { canSetKanban, kanbanGroupConfigure } = useMemo(() => {
        if (viewType === 'kanban') {
            const { canSetKanban, kanbanGroupConfigure = { groupByFieldId: '', groupConfig: [] } } = config
            return { canSetKanban, kanbanGroupConfigure }
        }
        return {
            canSetKanban: false,
            kanbanGroupConfigure: { groupByFieldId: '', groupConfig: [] }
        }
    }, [config, viewType])

    const { viewLayout } = baseBreakPoint
    const { layout = CUSTOM_VIEW_LAYOUT.waterfall, direction = DIRECTION.vertical, wrap = false } = viewLayout || {}
    const isCustomViewWrappedGrid =
        viewType === 'custom' && layout === CUSTOM_VIEW_LAYOUT.grid && direction === DIRECTION.horizontal && wrap === true
    const { groupByFieldId: kanbanGroupByField, groupConfig: kanbanGroupConfig } = kanbanGroupConfigure
    const enableSort = useMemo(() => enableSortView.has(viewType) && canSort, [canSort, viewType])

    const enableDisplay = useMemo(() => enableDisplayView.has(viewType) && canDisplay, [canDisplay, viewType])
    const enableToolbar = useMemo(() => {
        if (!toolbar) {
            return
        }
        return toolbar.length > 0
    }, [toolbar])

    const isCustomViewHorizontal =
        viewType === 'custom' && layout === CUSTOM_VIEW_LAYOUT.grid && direction === DIRECTION.horizontal && !wrap
    const isCustomViewCarousel = viewType === 'custom' && layout === CUSTOM_VIEW_LAYOUT.carousel
    const isCustomViewCalendar =
        viewType === 'custom' && (layout === CUSTOM_VIEW_LAYOUT.weekCalendar || layout === CUSTOM_VIEW_LAYOUT.monthCalendar)
    const isCustomViewComment = viewType === 'custom' && layout === CUSTOM_VIEW_LAYOUT.comment
    const isCustomViewTree = viewType === 'custom' && layout === CUSTOM_VIEW_LAYOUT.tree
    const canUseGroup = Boolean(canGroup) && !isCustomViewCarousel
    const canUsePagination = Boolean(canPaginate) && !isCustomViewCarousel && !isCustomViewWrappedGrid
    const isGalleryViewHorizontal = viewType === 'gallery' && direction === DIRECTION.horizontal
    const isShowViewSettingToolbar = useMemo(
        () =>
            enableToolbar ||
            canImport ||
            canSearch ||
            enableDisplay ||
            canUseGroup ||
            enableSort ||
            showTitle ||
            (quickFilterData && quickFilterData?.rules?.length > 0),
        [canImport, canSearch, canUseGroup, enableDisplay, enableSort, enableToolbar, quickFilterData, showTitle]
    )
    const isHasPagination = useMemo(
        () =>
            enablePagination.has(viewType) &&
            !isCustomViewHorizontal &&
            !isCustomViewWrappedGrid &&
            !isCustomViewCarousel &&
            !isGalleryViewHorizontal &&
            !isCustomViewCalendar &&
            !isCustomViewComment &&
            !isCustomViewTree,
        [
            isCustomViewCalendar,
            isCustomViewCarousel,
            isCustomViewComment,
            isCustomViewHorizontal,
            isCustomViewWrappedGrid,
            isCustomViewTree,
            isGalleryViewHorizontal,
            viewType
        ]
    )

    const settingColumns = useMemo(
        () =>
            getViewColumns({
                blockId,
                tableProps,
                value: viewFieldSettings,
                schema
            }),
        [blockId, schema, tableProps, viewFieldSettings]
    )

    // 用户字段设置后的tableProps
    const columns = useMemo(() => {
        if (!tablePropsCache || tablePropsCache.length === 0 || !canDisplay) {
            return settingColumns.filter(item => {
                return item.visible
            })
        }

        const columnsCache = tablePropsCache.reduce<ViewField[]>((prev, cur) => {
            const field = find(({ fieldId }) => fieldId === cur.id, settingColumns)
            if (field) {
                prev.push({
                    ...field,
                    visible: cur.visible
                })
            }
            return prev
        }, [])
        return settingColumns.reduce<ViewField[]>((prev, cur) => {
            if (!cur.visible) {
                return prev
            }
            const field = find(({ fieldId }) => fieldId === cur.fieldId, prev)
            if (!field) {
                prev.push({
                    ...cur,
                    visible: false
                })
            }
            return prev
        }, columnsCache)
    }, [tablePropsCache, canDisplay, settingColumns])

    // 合并校验后的选项配置
    const mergedSortRule = useMemo(() => {
        if (!kanbanGroupByField) {
            return
        }

        const currentViewCache = cachedKanbanSort?.[kanbanGroupByField] ?? []

        if (!kanbanGroupConfig && (!currentViewCache || currentViewCache.length === 0)) {
            return
        }

        const field = schema[kanbanGroupByField]
        if (!field) {
            return
        }
        const completeCurrentViewCache = getGroupVisibleWithLabelOptions(field, currentViewCache, { personOptions })
        if (!kanbanGroupConfig) {
            return completeCurrentViewCache
        }

        const completeKanbanVisibleConfig = mergeGroupVisibleOptions(
            getGroupVisibleWithLabelOptions(field, kanbanGroupConfig, { personOptions }),
            getGroupAllVisibleOptions(field, { personOptions })
        )

        if (completeCurrentViewCache.length === 0) {
            return completeKanbanVisibleConfig
        }

        return mergeGroupVisibleOptions(completeCurrentViewCache, completeKanbanVisibleConfig)
    }, [kanbanGroupByField, cachedKanbanSort, kanbanGroupConfig, schema, personOptions])

    const groupConfigure: GroupConfigure = useMemo(
        () => ({
            groupByFieldId: kanbanGroupByField,
            groupConfig: mergedSortRule || []
        }),
        [kanbanGroupByField, mergedSortRule]
    )

    return (
        <>
            {isShowViewSettingToolbar && (
                <ViewSettingToolbar
                    appId={appId}
                    envId={envId}
                    uniqueId={uniqueId}
                    dsId={pointer}
                    toolbar={toolbar}
                    viewType={viewType}
                    showTitle={showTitle}
                    title={title}
                    search={viewIndependentDataOmitRecord.search}
                    dataSource={dataSource}
                    dataSourceList={dataSourceList}
                    columns={columns}
                    settingColumns={settingColumns}
                    userImportSetting={userImportSetting}
                    quickFilter={quickFilterData}
                    userSortSetting={userSortSetting}
                    sort={sortCache}
                    enableSetKanban={canSetKanban}
                    enableGroup={canUseGroup}
                    group={groupCache}
                    enableSearch={canSearch}
                    enableImport={canImport}
                    enableSort={enableSort}
                    enableDisplay={enableDisplay}
                    kanbanGroupConfig={groupConfigure}
                    styles={{
                        root: {
                            padding: 0
                        }
                    }}
                    onSearch={onSearch}
                    onFilter={onFilter}
                    onChangeSorter={onChangeSorter}
                    onChangeDisplay={onChangeDisplay}
                    onChangeGroup={onChangeGroup}
                    onAppend={onAppend}
                    onActionTrigger={onToolbarActionTrigger}
                    onRenderButtonTitle={onRenderButtonTitle}
                    onKanbanGroupConfigChange={v => {
                        if (!kanbanGroupByField) {
                            return
                        }
                        onChangeCachedKanbanSort?.({
                            [kanbanGroupByField]: v.groupConfig.map(item => ({ value: item.value, visible: item.visible }))
                        })
                    }}
                />
            )}
            {canUseGroup && tabList && tabList.length > 0 && <TableTab tabList={tabList} value={tabValue} onTabChange={onTabChange} />}
            {children}
            {canUsePagination && isHasPagination && (
                <PaginationFc
                    isCustomViewCalendar={isCustomViewCalendar}
                    uniqueId={uniqueId}
                    top={12}
                    enablePadding
                    pagination={{
                        ...pagination,
                        currentPage: viewIndependentDataOmitRecord.currentPage,
                        rowTotal: viewIndependentDataOmitRecord.rowTotal
                    }}
                    activeStyle={{
                        background: 'var(--color-app-main-tint)',
                        color: 'var(--color-app-main)'
                    }}
                    onChangePageNum={onChangePageNum}
                />
            )}
        </>
    )
}
